/**
 * Runs in the Project DO (§10): RequestRun (deduplicated on the run's idempotency key), lifecycle facts relayed by
 * the hub, MCP reports, and the truthful end. Every runner write is fenced on the run's epoch (§10.5).
 */
import { LeaseTiming, routeAgents } from "@minga/protocol"
import { isWorkingMember, itemAskedBy, runOfLeaver, runPrincipalOf } from "../multiplayer"
import { mergePlacement } from "../placement"
import { readiness } from "../readiness"
import { resolveRouting, routedFamily } from "../routing"
import { acceptOutcome, closeByMerge, lostRunnerStop, transitionsAfterRunEnded } from "../runs"
import { agentOf, areaOf, isRunEnded, itemOf, leadOf, must, proposalOf, runOf } from "../state"
import { goalsOf, topItemOf } from "../tree"
import type {
  AgentKind,
  Command,
  Epoch,
  Hands,
  Ids,
  Item,
  Placement,
  Principal,
  ProjectState,
  Run,
} from "../types"
import { recordedUsage, turnAskedBy } from "../usage"
import { byAgentOf, crossModelPlacement, resolveWorker, runFamilyOf, turnAccess } from "../workers"
import { openShareCards } from "./share-login"
import { forbidden, invalid, notFound, reject, type Tx } from "./tx"

type Cmd<T extends Command["_tag"]> = Extract<Command, { _tag: T }>

/** Fencing: accept iff epoch ≥ highest seen, the run has not ended, and (after a LeaseClosed) epoch > closed epoch. */
export const checkFence = (
  highestSeen: number,
  closedEpoch: number | null,
  ended: boolean,
  presented: number,
): "accept" | "stale" =>
  ended || presented < highestSeen || (closedEpoch !== null && presented <= closedEpoch) ? "stale" : "accept"

const fence = (_tx: Tx, run: Run, presented: number) => {
  if (checkFence(run.epoch, null, isRunEnded(run), presented) === "stale")
    reject({
      _tag: "StaleLease",
      runId: run.id,
      presented: presented as Epoch,
      current: run.epoch,
      ended: isRunEnded(run),
    })
}

const requireRun = (tx: Tx, id: Ids["RunId"]): Run => runOf(tx.state, id) ?? notFound({ _tag: "run", id })

const handsPlacement = (hands: Hands): Placement | null =>
  hands.agent ? { requires: { agent: [hands.agent] }, prefers: [], affinity: null, exclusive: [] } : null

/** The run's frozen attribution for an item: its goals (through the top-level item), the top item, area and owner agent. */
const attributionOf = (s: ProjectState, t: Item, agentId: Ids["AgentId"] | null): Run["attribution"] => ({
  goalIds: [...goalsOf(s, t)],
  topItemId: topItemOf(s, t).id,
  areaId: t.areaId,
  itemId: t.id,
  agentId,
})

/** A pending rate_limited retry pins the same runner and profile (never another account). */
const retryPin = (tx: Tx, t: Item): Placement | null => {
  if (!t.retry?.pinProfile) return null
  const last = t.lastRunId ? runOf(tx.state, t.lastRunId) : undefined
  const runnerId = last?.executor?._tag === "runner" ? last.executor.runnerId : undefined
  return {
    requires: { profile: t.retry.pinProfile, ...(runnerId ? { runner: runnerId } : {}) },
    prefers: [],
    affinity: null,
    exclusive: [],
  }
}

const combine = (...ps: ReadonlyArray<Placement | null>): Placement | null => {
  let acc: Placement | null = null
  for (const p of ps) {
    if (!p) continue
    const r = mergePlacement(acc, p, null)
    if ("invalid" in r) invalid(r.invalid, "placement")
    acc = r.ok
  }
  return acc
}

/**
 * Decision 0116: an item's runner or profile pin (its `placement`) holds over a route that names another place (a
 * project rule, the item's or a retry's route): the route's conflicting runner and profile are dropped, its CLI, model
 * and avoid list stay. Before, the two pins made the dispatch invalid.
 */
export const yieldToPin = (pin: Placement | null, route: Placement | null): Placement | null => {
  if (!pin || !route) return route
  const want = pin.requires
  const rq: Record<string, unknown> = { ...route.requires }
  let changed = false
  if (want.runner !== undefined && rq.runner !== undefined && rq.runner !== want.runner) {
    delete rq.runner
    delete rq.profile
    changed = true
  }
  if (want.profile !== undefined && rq.profile !== undefined && rq.profile !== want.profile) {
    delete rq.profile
    changed = true
  }
  return changed ? { ...route, requires: rq as Placement["requires"] } : route
}

const effectivePlacement = (
  tx: Tx,
  areaId: Ids["AreaId"] | null,
  item: Placement | null,
  extra: Placement | null,
): Placement => {
  const area = areaId ? areaOf(tx.state, areaId) : undefined
  const r = mergePlacement(area?.placement ?? null, item, extra)
  if ("invalid" in r) invalid(`placement: ${r.invalid}`, "placement")
  return r.ok
}

/**
 * System constraints on code runs (decision 0011, C2), applied after the merge and before the run is frozen. Both are
 * deterministic in the state: a worker or validator in a single-repo project requires that repo when nothing else
 * named one, and a worker that works in a repo requires the tool `gh` (W25: the runner opens its PR with it), so a
 * runner without a working `gh` shows `missing_tool gh` instead of failing late.
 */
const codeRunPlacement = (
  tx: Tx,
  kind: "worker" | "validator",
  placement: Placement,
  repo: "project" | "none",
): Placement => {
  // K18: a "no repo" preset skips the single-repo default (worker and validators); a repo named explicitly by the area,
  // task or retry placement still applies, and then the worker still needs gh (its worktree opens a draft PR)
  const repos = tx.state.project?.repos ?? []
  let requires = placement.requires
  if (repo === "project" && (requires.repos?.length ?? 0) === 0 && repos.length === 1)
    requires = { ...requires, repos: [...repos] }
  if (
    kind === "worker" &&
    (requires.repos?.length ?? 0) > 0 &&
    !(requires.tools ?? []).some((t) => t === "gh" || t.startsWith("gh@"))
  )
    requires = { ...requires, tools: [...(requires.tools ?? []), "gh"] }
  return requires === placement.requires ? placement : { ...placement, requires }
}

/**
 * Wave E (decision 0063, spec §3.2): the principal of a worker or validator run follows its asker (wave F's
 * `itemAskedBy`: the item's asker, else the person who created it, else the supervising agent). A person who left
 * starts nothing: the item waits for someone to edit or retry it.
 */
const workPrincipal = (
  tx: Tx,
  kind: "worker" | "validator",
  t: Item,
  owner: { readonly id: Ids["AgentId"]; readonly ownerId: Ids["UserId"] },
): { readonly ownerId: Ids["UserId"]; readonly askedBy: Principal } => {
  const askedBy = itemAskedBy(t, owner.id)
  const ownerId = runPrincipalOf(tx.state, { kind, askedBy, keeper: owner.ownerId })
  if (ownerId === null)
    invalid(`${t.ref} was asked by someone who left: edit it or Retry to run it as you`, "itemId")
  return { ownerId, askedBy }
}

const existingByKey = (tx: Tx, key: string): Run | undefined =>
  tx.state.runs.find((r) => r.idempotencyKey === key)

export const requestRun = (tx: Tx, c: Cmd<"RequestRun">): void => {
  const s = tx.state
  if (runOf(s, c.runId)) invalid("run id already exists", "runId")
  const a = tx.actor
  const base = {
    visibility: "workspace" as const,
    id: c.runId,
    ref: tx.nextRef("R"),
    kind: c.kind,
    subjectRunId: null as Ids["RunId"] | null,
    publish: null as Run["publish"],
    wakeBatchId: null as Run["wakeBatchId"],
    context: c.context,
    state: { _tag: "requested" as const },
    epoch: 0 as Epoch,
    prestartReleases: 0,
    executor: c.executor ?? null,
    claimed: null,
    verdict: null,
    outcome: null,
    downgrade: null,
    stopReason: null,
    error: null,
    usage: [],
    sessionIn: null,
    sessionOut: null,
    taint: c.context.taint,
    requestedAt: tx.now,
    startedAt: null,
    endedAt: null,
  }
  const dupCheck = (key: string) => {
    const d = existingByKey(tx, key)
    if (d)
      reject({
        _tag: "Duplicate",
        existing: { _tag: "run", id: d.id },
        reason: `run ${d.ref} already has key ${key}`,
      })
  }
  switch (c.kind) {
    case "worker": {
      if (a._tag !== "system") forbidden("actor_kind", "worker runs are dispatched by the scheduler")
      const t = c.itemId ? itemOf(s, c.itemId) : undefined
      if (!t) invalid("a worker run needs an item", "itemId")
      if (t.activeRunId)
        reject({
          _tag: "Duplicate",
          existing: { _tag: "run", id: t.activeRunId },
          reason: `${t.ref} already has a live attempt`,
        })
      const attempt = t.attempts + 1
      const key = `worker:${t.id}:${attempt}`
      dupCheck(key)
      const r = readiness(s, t.id, tx.now)
      if (r._tag !== "ready") invalid(`${t.ref} is not ready (${r._tag})`, "itemId")
      const owner = must(t.owner?._tag === "agent" ? agentOf(s, t.owner.agentId) : undefined, "owner agent")
      const w = resolveWorker(s, t, owner, "worker")
      const hands = w.hands
      const placement = codeRunPlacement(
        tx,
        "worker",
        effectivePlacement(
          tx,
          t.areaId,
          t.placement,
          combine(
            combine(handsPlacement(hands), retryPin(tx, t)),
            yieldToPin(t.placement, combine(w.routing.placement, w.choicePlacement)),
          ),
        ),
        w.repo,
      )
      const who = workPrincipal(tx, "worker", t, owner)
      const run: Run = {
        ...base,
        ownerId: who.ownerId,
        agentId: owner.id,
        itemId: t.id,
        attempt,
        idempotencyKey: key,
        attribution: attributionOf(s, t, owner.id),
        placement,
        hands,
        sink: { _tag: "run_do" },
        worker: { ref: tx.nextWorkerRef("W"), supervisorId: owner.id, preset: w.presetKey },
        byAgent: byAgentOf(w),
        ...(w.routing.rule ? { routing: w.routing.rule } : {}),
        ...(w.routeChoice ? { routeChoice: w.routeChoice } : {}),
        ...accessField(w.access),
        // the description revision this attempt reads (spec_changed compares against it)
        specRev: t.specRev.n,
        askedBy: who.askedBy,
      }
      tx.emit({ _tag: "RunRequested", run, ...branchOf(c) })
      tx.emit({
        _tag: "ItemStatusChanged",
        itemId: t.id,
        from: "todo",
        to: "in_progress",
        cause: { _tag: "attempt_started", runId: run.id },
        blocked: null,
        resolution: null,
      })
      return
    }
    case "validator": {
      if (a._tag !== "system") forbidden("actor_kind", "validator runs are dispatched by the scheduler")
      const t = c.itemId ? itemOf(s, c.itemId) : undefined
      if (!t) invalid("a validator run needs an item", "itemId")
      if (t.status !== "in_review" || t.review.stages[t.review.stage] !== "validator")
        invalid(`${t.ref} is not at a validator stage`, "itemId")
      // decision 0156 (REP-66): no validator round starts on a merged head; the merge ends the review loop
      if (closeByMerge(tx, t.id)) return
      const subject = c.subjectRunId ? runOf(s, c.subjectRunId) : undefined
      if (!subject || subject.itemId !== t.id)
        invalid("the subject run must be a run of this item", "subjectRunId")
      const round = t.review.round
      const prior = s.runs.filter((r) => r.kind === "validator" && r.itemId === t.id && r.attempt === round)
      const live = prior.find((r) => !isRunEnded(r))
      if (live)
        reject({
          _tag: "Duplicate",
          existing: { _tag: "run", id: live.id },
          reason: "a validator for this round is running",
        })
      const key = `validator:${t.id}:${round}:${prior.length + 1}`
      dupCheck(key)
      const owner =
        t.owner?._tag === "agent"
          ? (agentOf(s, t.owner.agentId) ?? notFound({ _tag: "agent", id: t.owner.agentId }))
          : invalid("the item has no owner agent to supervise its validator", "itemId")
      const subjectKind: AgentKind | null = runFamilyOf(subject)
      // decision 0147: the other family is a strong preference, never a requirement (a missing family never waits)
      const cross: Placement | null =
        t.review.crossModel && subjectKind ? crossModelPlacement(subjectKind) : null
      // settings wave §5.5 step 4 (replaces C4): a validator's family is decided by its lease (0147).
      // A preset change "changes the next attempt only": the validator follows the preset its subject ran with, and
      // works in a repo exactly when the subject did (K18), not the item's current choice.
      const w = resolveWorker(s, t, owner, "validator", subjectKind, subject.worker?.preset ?? null)
      const repoMode: "project" | "none" = subject.worker
        ? (subject.placement.requires.repos?.length ?? 0) > 0
          ? "project"
          : "none"
        : w.repo
      const hands: Hands = w.hands
      const who = workPrincipal(tx, "validator", t, owner)
      const run: Run = {
        ...base,
        ownerId: who.ownerId,
        agentId: owner.id,
        itemId: t.id,
        subjectRunId: subject.id,
        attempt: Math.max(1, round),
        idempotencyKey: key,
        attribution: attributionOf(s, t, owner.id),
        placement: codeRunPlacement(
          tx,
          "validator",
          effectivePlacement(
            tx,
            t.areaId,
            t.placement,
            combine(
              combine(w.routing.required || w.routeChoice ? null : cross, handsPlacement(hands)),
              yieldToPin(t.placement, combine(w.routing.placement, w.choicePlacement)),
            ),
          ),
          repoMode,
        ),
        hands,
        sink: { _tag: "run_do" },
        worker: { ref: tx.nextWorkerRef("V"), supervisorId: owner.id, preset: w.presetKey },
        byAgent: byAgentOf(w),
        ...(w.routing.rule ? { routing: w.routing.rule } : {}),
        ...(w.routeChoice ? { routeChoice: w.routeChoice } : {}),
        ...accessField(w.access),
        specRev: t.specRev.n,
        askedBy: who.askedBy,
      }
      tx.emit({ _tag: "RunRequested", run, ...branchOf(c) })
      return
    }
    case "turn": {
      const agentId = c.agentId ?? (a._tag === "agent" ? a.agentId : undefined)
      if (!agentId) invalid("a turn needs an agent", "agentId")
      if (a._tag === "agent" && a.agentId !== agentId)
        forbidden("not_owner", "an agent requests only its own turns")
      if (a._tag !== "agent" && a._tag !== "system")
        forbidden("actor_kind", "turns are requested by the Agent DO")
      if (!c.wakeBatchId) invalid("a turn answers a wake batch", "wakeBatchId")
      const agent = agentOf(s, agentId) ?? notFound({ _tag: "agent", id: agentId })
      if (agent.status !== "active") invalid(`agent is ${agent.status}`, "agentId")
      const live = s.runs.find((r) => r.kind === "turn" && r.agentId === agentId && !isRunEnded(r))
      if (live)
        reject({
          _tag: "Duplicate",
          existing: { _tag: "run", id: live.id },
          reason: "one turn at a time per agent",
        })
      const key = `turn:${agentId}:${c.wakeBatchId}`
      dupCheck(key)
      const mind = agent.mind
      const routing = resolveRouting(s.project?.routing, { kind: "turn" })
      const native =
        mind._tag === "runner" ||
        routing.rule?.runnerId !== undefined ||
        routing.rule?.profileId !== undefined
      const originalFamily =
        mind._tag === "runner" ? mind.agent : mind.provider === "anthropic" ? "claude" : "codex"
      // decision 0118: a route may allow several CLIs ("any of")
      const routed = routeAgents(routing.rule)
      const family: AgentKind =
        !native && c.executor?._tag === "platform"
          ? c.executor.provider === "anthropic"
            ? "claude"
            : "codex"
          : (routedFamily(routing, originalFamily) ?? originalFamily)
      const choice = routing.byAgent[family]
      const flexible = native && !routing.required && routed.length > 0
      const apiFallback = !native && !routing.required && routed.length > 0 && !routed.includes(family)
      const model = native
        ? (choice?.model ?? (mind._tag === "runner" && family === originalFamily ? mind.model : null))
        : ((!apiFallback ? routing.rule?.apiModel : null) ??
          (c.executor?._tag === "platform" ? c.executor.model : null))
      const effort =
        choice?.effort ?? (mind._tag === "runner" && family === originalFamily ? mind.effort : null)
      let placement: Placement = { requires: {}, prefers: [], affinity: null, exclusive: [] }
      if (native) {
        const r = mergePlacement(
          null,
          {
            requires: {
              ...(mind._tag === "runner" ? mind.requires : {}),
              ...(!flexible ? { agent: [family] } : {}),
              ...(mind._tag === "runner" && mind.profileId ? { profile: mind.profileId } : {}),
            },
            // decision 0118: a flexible turn still allows the mind's own CLI (an opt-in one like Antigravity too)
            prefers: flexible ? [{ match: { agent: [family] }, weight: 0 }] : [],
            affinity: { sessionOf: agentId, strength: "soft" },
            exclusive: [],
          },
          routing.placement,
        )
        if ("invalid" in r) invalid(r.invalid, "mind")
        placement = r.ok
      } else if (mind._tag === "platform") {
        if (c.executor?._tag !== "platform") invalid("a platform turn names its executor", "executor")
        if (
          routing.required && routed.length > 0
            ? !routed.includes(family)
            : family !== originalFamily && !routed.includes(family)
        )
          invalid(
            "the platform runtime must resolve the routed provider and API model before requesting the run",
            "executor.provider",
          )
      }
      const askedBy = turnAskedBy(c.askedBy, agentId).askedBy
      if (c.workScope?.exclusive && c.workScope.requests.length !== 1)
        invalid("an exclusive work scope names one responsibility revision", "workScope")
      for (const scope of c.workScope?.requests ?? []) {
        const responsibility = s.workRequests?.find((r) => r.id === scope.requestId)
        if (
          !responsibility ||
          responsibility.target.projectId !== s.project?.id ||
          scope.revision > responsibility.revision
        )
          invalid("work scope names an incoming responsibility revision", "workScope")
      }
      const run: Run = {
        ...base,
        ownerId: agent.ownerId,
        agentId,
        itemId: null,
        wakeBatchId: c.wakeBatchId,
        ...(c.wakeReasons ? { wakeReasons: c.wakeReasons } : {}),
        ...(c.workScope ? { workScope: c.workScope } : {}),
        attempt: 1,
        idempotencyKey: key,
        attribution: { goalIds: [], topItemId: null, areaId: agent.areaId, itemId: null, agentId },
        placement,
        ...(routing.rule ? { routing: routing.rule } : {}),
        ...(native ? { executor: null } : {}),
        ...(!native && c.executor?._tag === "platform"
          ? {
              executor: {
                ...c.executor,
                provider: family === "claude" ? "anthropic" : "openai",
                model: model ?? "",
                credentialId: family === originalFamily ? c.executor.credentialId : null,
              },
            }
          : {}),
        hands: native
          ? flexible
            ? { agent: null, model: null, effort: null }
            : { agent: family, model, effort }
          : { agent: null, model, effort: null },
        ...(native
          ? {
              byAgent: Object.fromEntries(
                (["claude", "codex", "antigravity", "muse"] as const)
                  .filter(
                    (f) =>
                      (f !== "antigravity" && f !== "muse") ||
                      (mind._tag === "runner" && mind.agent === f) ||
                      routing.byAgent[f],
                  )
                  .map((f) => [
                    f,
                    {
                      model:
                        routing.byAgent[f]?.model ??
                        (mind._tag === "runner" && mind.agent === f ? mind.model : null),
                      effort:
                        routing.byAgent[f]?.effort ??
                        (mind._tag === "runner" && mind.agent === f ? mind.effort : null),
                    },
                  ]),
              ),
            }
          : {}),
        sink: { _tag: "agent", agentId },
        // Persistent turns inherit their configured scope across people, events and schedules.
        ...accessField(turnAccess(agent, askedBy)),
        // decision 0048: who started the turn (absent = a person, as before this wave)
        ...(c.startedBy !== undefined ? { startedBy: c.startedBy } : {}),
        // Wave A §4.0: the chain of the batch this turn answers (the Project DO's `batchChain`); absent = 0
        ...(c.chain ? { chain: c.chain } : {}),
        // wave F: who asked (the Project DO's `askedByOfBatch`); never a reason to refuse the turn
        askedBy,
      }
      tx.emit({ _tag: "RunRequested", run })
      return
    }
    case "publish": {
      if (a._tag !== "system") forbidden("actor_kind", "publish runs are dispatched by the scheduler")
      if (!c.publish) invalid("a publish run names its approved proposal", "publish")
      const p =
        proposalOf(s, c.publish.proposalId) ?? notFound({ _tag: "proposal", id: c.publish.proposalId })
      if (p.body._tag !== "outward_action" || p.state !== "approved" || p.revision !== c.publish.revision)
        invalid("only an approved outward action at its approved revision is published", "publish")
      const key = `publish:${p.id}:${p.revision}`
      dupCheck(key)
      const t = itemOf(s, p.body.itemId)
      const ownerAgentId = t?.owner?._tag === "agent" ? t.owner.agentId : null
      const placement: Placement = p.body.executor
        ? { requires: p.body.executor, prefers: [], affinity: null, exclusive: [] }
        : { requires: {}, prefers: [], affinity: null, exclusive: [] }
      const run: Run = {
        ...base,
        ownerId: p.ownerId,
        agentId: ownerAgentId,
        itemId: p.body.itemId,
        publish: c.publish,
        attempt: 1,
        idempotencyKey: key,
        attribution: t
          ? attributionOf(s, t, ownerAgentId)
          : { goalIds: [], topItemId: null, areaId: null, itemId: null, agentId: null },
        placement,
        hands: { agent: null, model: null, effort: null },
        sink: { _tag: "run_do" },
        executor: c.executor ?? (p.body.executor ? null : { _tag: "gateway" }),
        // wave F: the person who approved the outward action, else the proposal's owner
        askedBy:
          p.resolution?.by._tag === "user"
            ? { _tag: "user", userId: p.resolution.by.userId }
            : { _tag: "user", userId: p.ownerId },
      }
      tx.emit({ _tag: "RunRequested", run })
      return
    }
  }
}

/** `Run.access` only when a request narrows something (absent otherwise). */
const accessField = (a: Run["access"] | undefined): { access?: NonNullable<Run["access"]> } =>
  a ? { access: a } : {}

/** RequestRun.branch → RunRequested.branch (display only; absent stays absent). */
const branchOf = (c: Cmd<"RequestRun">): { branch?: string | null } =>
  c.branch === undefined ? {} : { branch: c.branch }

export const cancelRun = (tx: Tx, c: Cmd<"CancelRun">): void => {
  const run = requireRun(tx, c.runId)
  if (isRunEnded(run))
    reject({ _tag: "StaleLease", runId: run.id, presented: run.epoch, current: run.epoch, ended: true })
  const a = tx.actor
  // wave E (decision 0063, answer 6): any project member stops any run; the event's actor records who stopped it
  if (a._tag === "user" && !isWorkingMember(tx.state, a.userId))
    forbidden("not_owner", "only a project member stops a run")
  // wave E (spec §5.4): `system:membership` stops only a run whose asker or principal has left, reason `member_left`
  if (a._tag === "system" && !(c.reason === "member_left" && runOfLeaver(tx.state, run)))
    forbidden("not_owner", "membership stops only the runs of a person who left (reason member_left)")
  // decision 0032: the project lead also stops worker and validator runs of any item, never another agent's turn
  if (
    a._tag === "agent" &&
    run.agentId !== a.agentId &&
    !(leadOf(tx.state)?.id === a.agentId && (run.kind === "worker" || run.kind === "validator"))
  )
    forbidden(
      "not_owner",
      "only the item's owner or the project lead cancels a run (never another agent's turn)",
    )
  tx.emit({ _tag: "RunCancelRequested", runId: run.id, reason: c.reason, requestedAt: tx.now })
}

/**
 * The lease ended before "running": the run goes back to the hub's queue (next epoch, capped). Decision 0150: with the
 * hub's real cause (a release's reason) when it sent one; `expired` otherwise (an older hub, or a lease that ran out).
 */
const requeueIfPreparing = (
  tx: Tx,
  run: Run,
  reason: "ceiling" | "prepare_failed" | "shutdown" | "expired" = "expired",
) => {
  if (run.state._tag === "leased" && run.state.phase === "preparing") {
    tx.emit({
      _tag: "RunRequeued",
      runId: run.id,
      epoch: run.epoch,
      reason,
      prestartReleases: run.prestartReleases + 1,
    })
    return true
  }
  return false
}

export const recordRunWaiting = (tx: Tx, c: Cmd<"RecordRunWaiting">): void => {
  const run = requireRun(tx, c.runId)
  if (isRunEnded(run))
    reject({ _tag: "StaleLease", runId: run.id, presented: run.epoch, current: run.epoch, ended: true })
  if (run.state._tag === "leased" && run.state.phase !== "preparing")
    invalid("a started run can't wait: its loss ends it", "runId")
  requeueIfPreparing(tx, run, c.requeueReason)
  // wave E (decision 0063): lend candidates only for a worker or validator run; a turn keeps its share candidates
  // (0128 addendum: someone's login it would run on if shared)
  const lendable =
    run.kind === "worker" || run.kind === "validator"
      ? (c.lendable ?? [])
      : run.kind === "turn"
        ? (c.lendable ?? []).filter((x) => x.profileId !== undefined)
        : []
  tx.emit({
    _tag: "RunWaiting",
    runId: run.id,
    unmet: c.unmet,
    since: c.since,
    ...(lendable.length > 0 ? { lendable } : {}),
  })
  if (run.kind === "turn") openShareCards(tx, must(runOf(tx.state, run.id), "run"))
}

/**
 * The hub took the lease back from a runner that can't run it (decision 0039: no `item_model`) and queued the run
 * again. No attempt is counted: the item keeps its live run, which the next capable runner starts at a new epoch.
 */
export const recordRunRequeued = (tx: Tx, c: Cmd<"RecordRunRequeued">): void => {
  const run = requireRun(tx, c.runId)
  if (isRunEnded(run))
    reject({ _tag: "StaleLease", runId: run.id, presented: c.epoch, current: run.epoch, ended: true })
  if (run.state._tag !== "leased" || c.epoch !== run.epoch)
    invalid("the run is not leased at this epoch: nothing to requeue", "epoch")
  tx.emit({
    _tag: "RunRequeued",
    runId: run.id,
    epoch: run.epoch,
    reason: c.reason,
    prestartReleases: run.prestartReleases,
  })
}

export const recordRunLeased = (tx: Tx, c: Cmd<"RecordRunLeased">): void => {
  const run = requireRun(tx, c.runId)
  if (isRunEnded(run) || c.epoch <= run.epoch)
    reject({
      _tag: "StaleLease",
      runId: run.id,
      presented: c.epoch,
      current: run.epoch,
      ended: isRunEnded(run),
    })
  if (run.state._tag === "leased" && run.state.phase !== "preparing")
    invalid("a started run is never re-leased", "runId")
  if (run.prestartReleases >= LeaseTiming.MAX_PRESTART_RELEASES)
    invalid("the pre-start re-lease cap is reached: end the run", "runId")
  requeueIfPreparing(tx, run, c.requeueReason)
  tx.emit({ _tag: "RunLeased", runId: run.id, epoch: c.epoch, executor: c.executor })
}

export const recordRunStarted = (tx: Tx, c: Cmd<"RecordRunStarted">): void => {
  const run = requireRun(tx, c.runId)
  const a = tx.actor
  if (a._tag === "agent") {
    // platform turns: the Agent DO reports its own turn (no lease, epoch 0)
    if (run.kind !== "turn" || run.agentId !== a.agentId || run.executor?._tag !== "platform")
      forbidden("actor_kind", "an agent reports only its own platform turns")
    if (run.state._tag !== "requested") invalid("the turn has already started", "runId")
    tx.emit({ _tag: "RunStarted", runId: run.id, epoch: run.epoch, sessionIn: c.sessionIn, taint: run.taint })
    return
  }
  fence(tx, run, c.epoch)
  if (c.epoch !== run.epoch || run.state._tag !== "leased" || run.state.phase !== "preparing")
    invalid("the run is not leased at this epoch, or has already started", "epoch")
  tx.emit({ _tag: "RunStarted", runId: run.id, epoch: run.epoch, sessionIn: c.sessionIn, taint: run.taint })
}

export const recordRunMilestone = (tx: Tx, c: Cmd<"RecordRunMilestone">): void => {
  const run = requireRun(tx, c.runId)
  fence(tx, run, c.epoch)
  tx.emit({ _tag: "RunMilestone", runId: run.id, epoch: run.epoch, kind: c.kind, text: c.text, ref: c.ref })
}

/** The run of an MCP caller (agent turn or worker), fenced on the token's epoch. */
const callerRun = (tx: Tx): Run => {
  const a = tx.actor
  if (a._tag !== "agent" && a._tag !== "worker") forbidden("actor_kind", "only a run reports about itself")
  const run = requireRun(tx, a.runId)
  fence(tx, run, tx.ctx.epoch ?? run.epoch)
  return run
}

export const reportOutcome = (tx: Tx, c: Cmd<"ReportOutcome">): void => {
  const run = callerRun(tx)
  if (run.kind === "validator") forbidden("actor_kind", "validators report verdicts, not outcomes")
  if (run.kind === "publish")
    forbidden("actor_kind", "publish runs report results through RecordOutwardResult")
  tx.emit({ _tag: "RunOutcomeReported", runId: run.id, epoch: run.epoch, outcome: c.outcome })
}

export const reportVerdict = (tx: Tx, c: Cmd<"ReportVerdict">): void => {
  const run = callerRun(tx)
  if (run.kind !== "validator") forbidden("actor_kind", "only validator runs report verdicts")
  tx.emit({ _tag: "RunVerdictReported", runId: run.id, epoch: run.epoch, verdict: c.verdict })
}

export const raiseRunTaint = (tx: Tx, c: Cmd<"RaiseRunTaint">): void => {
  const run = requireRun(tx, c.runId)
  fence(tx, run, c.epoch)
  if (run.taint === "tainted") return // taint only rises; already tainted is a no-op
  tx.emit({ _tag: "RunTaintRaised", runId: run.id, epoch: run.epoch, source: c.source })
}

/** The truthful end: stop reason (mechanical) + claim + accepted outcome + downgrade, then the item transition. */
export const recordRunEnded = (tx: Tx, c: Cmd<"RecordRunEnded">): void => {
  const run = requireRun(tx, c.runId)
  const a = tx.actor
  if (
    a._tag === "agent" &&
    (run.kind !== "turn" || run.agentId !== a.agentId || run.executor?._tag !== "platform")
  )
    forbidden("actor_kind", "an agent reports only its own platform turns")
  if (
    a._tag === "system" &&
    a.process === "lease_reaper" &&
    c.stopReason !== "runner_lost" &&
    c.stopReason !== "sandbox_error" &&
    c.stopReason !== "policy_refused"
  )
    forbidden("actor_kind", "the lease reaper only ends runs it lost")
  fence(tx, run, c.epoch)
  // decision 0150: a lost lease (no contact with Minga) is a lost runner, never a stop someone asked for; decision 0158:
  // a run someone asked to stop that then ends lost is recorded as that stop
  const stopReason = lostRunnerStop(c.stopReason, c.error, run)
  const { accepted, downgrade } = acceptOutcome(tx.state, run, stopReason)
  const rateLimit = stopReason === "rate_limited" ? c.rateLimit : undefined
  // wave F (decision 0062): every row priced here, whatever runner sent it; an unacceptable payer is dropped, never a
  // reason to refuse the end
  const recorded = recordedUsage(a, c)
  tx.emit({
    _tag: "RunEnded",
    runId: run.id,
    epoch: run.epoch,
    stopReason,
    claimed: run.claimed,
    outcome: accepted,
    downgrade,
    verdict: run.verdict,
    usage: [...recorded.usage],
    error: c.error,
    sessionOut: c.sessionOut,
    transcriptRef: c.transcriptRef,
    // decision 0059: the worktree's head, only for a worker run with a worktree branch
    ...(c.head !== undefined && run.kind === "worker" && run.branch ? { head: c.head } : {}),
    ...(recorded.fundedBy !== undefined ? { fundedBy: recorded.fundedBy } : {}),
    ...(recorded.compute !== undefined ? { compute: [...recorded.compute] } : {}),
    // decision 0095: where it ran (the hub fills it from its own records)
    ...(c.place !== undefined ? { place: c.place } : {}),
    // decision 0149: what it used (the hub sanitized the runner's numbers)
    ...(c.resources !== undefined ? { resources: c.resources } : {}),
    // decision 0155: when a rate-limited run may run again (the hub's facts)
    ...(rateLimit ? { rateLimit } : {}),
  })
  // open permission prompts of the run are withdrawn when it ends (§9.8)
  for (const p of tx.state.proposals)
    if (
      p.body._tag === "permission" &&
      p.body.runId === run.id &&
      (p.state === "open" || p.state === "revision_requested")
    )
      tx.emit({ _tag: "ProposalWithdrawn", proposalId: p.id })
  transitionsAfterRunEnded(
    tx,
    must(runOf(tx.state, run.id), "run"),
    stopReason,
    accepted,
    c.error?.fingerprint ?? null,
    rateLimit
      ? { resetsAt: rateLimit.resetsAt, ...(rateLimit.window ? { window: rateLimit.window } : {}) }
      : {},
  )
}
